import { useEffect, useState } from "react";
import {
  type Band,
  type LightColor,
  type SlotRole,
  type StageConfig,
  type StagePreset,
  DEFAULT_STAGE,
  LIGHT_COLORS,
  SLOT_ROLES,
  STAGE_PRESETS,
} from "../../infra/api.js";
import { aggregateBandTraits, type WeightedSlotTraits } from "../../domain/index.js";
import { useBandStore } from "../../stores/bandStore.js";
import { Button } from "../../ui/Button.js";
import { SonicTraitsCard } from "./SonicTraitsCard.js";
import { LIGHT_HEX, Stage } from "./Stage.js";

const PRESET_LABELS: Record<StagePreset, string> = {
  underground: "Underground",
  jazz: "Jazz Club",
  openair: "Open Air",
  garage: "Garage",
  synthwave: "Synthwave",
  acoustic: "Acoustic",
};

const PRESET_BLURBS: Record<StagePreset, string> = {
  underground: "Porão vermelho-quente, vibe de show secreto",
  jazz: "Casa noturna art-deco, cortinas e fumaça",
  openair: "Festival ao ar livre com céu pôr-do-sol",
  garage: "Concreto exposto, paredes industriais",
  synthwave: "Neon retrofuturo magenta+ciano",
  acoustic: "Madeira clara, luzes de bistrô",
};

const LIGHT_LABELS: Record<LightColor, string> = {
  warm: "quente",
  cool: "frio",
  magenta: "magenta",
  green: "verde",
  white: "branco",
  amber: "âmbar",
};

const ROLE_LABELS: Record<SlotRole, string> = {
  vocal: "Vocal",
  guitar: "Guitarra",
  bass: "Baixo",
  drums: "Bateria",
  keys: "Teclado",
};

interface Props {
  band: Band;
  onClose: () => void;
}

export function StageEditor({ band, onClose }: Props): JSX.Element {
  const [config, setConfig] = useState<StageConfig>(band.stage_config ?? DEFAULT_STAGE);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setConfig(band.stage_config ?? DEFAULT_STAGE);
    setDirty(false);
  }, [band.stage_config]);

  function update<K extends keyof StageConfig>(key: K, value: StageConfig[K]): void {
    setConfig((c) => ({ ...c, [key]: value }));
    setDirty(true);
  }

  function updateWeight(role: SlotRole, value: number): void {
    setConfig((c) => ({ ...c, slotWeights: { ...c.slotWeights, [role]: value } }));
    setDirty(true);
  }

  // Optimistic recompute of the band's aggregated traits as the weights move,
  // mirroring what the API's resolver does — so the preview never lies.
  const previewTraits = aggregateBandTraits(
    band.slots.map(
      (s): WeightedSlotTraits => ({
        role: s.slot_role,
        traits: s.artist.sonic_traits ?? {},
        weight: config.slotWeights[s.slot_role] ?? 0,
      }),
    ),
  );

  async function onSave(): Promise<void> {
    setSaving(true);
    try {
      await useBandStore.getState().updateStage(band.id, config);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  }

  function onReset(): void {
    setConfig(DEFAULT_STAGE);
    setDirty(true);
  }

  return (
    <div className="flex flex-col gap-4 border border-border bg-tape p-5 shadow-sleeve">
      <header className="flex items-baseline justify-between border-b border-border pb-3">
        <div>
          <p className="catalog-num">CUSTOMIZAR · PALCO</p>
          <h3
            className="font-display text-2xl text-paper"
            style={{ fontVariationSettings: '"opsz" 48, "wght" 600, "SOFT" 80' }}
          >
            Direção de palco
          </h3>
        </div>
        <Button variant="ghost" onClick={onClose}>
          fechar
        </Button>
      </header>

      <div className="grid grid-cols-12 gap-5">
        {/* Live preview */}
        <div className="col-span-12 flex flex-col gap-4 lg:col-span-7">
          <div>
            <p className="catalog-num mb-2">PRÉVIA AO VIVO</p>
            <Stage band={band} playing={false} configOverride={config} height={280} size={84} />
          </div>
          <div className="border-l-2 border-oscillo bg-asphalt/60 px-3 py-3">
            <p className="catalog-num mb-2 text-oscillo">SOM DA BANDA · PRÉVIA</p>
            <SonicTraitsCard
              traits={previewTraits}
              emptyHint="Os artistas dos slots não têm traços — ou os pesos zeraram tudo."
            />
          </div>
        </div>

        {/* Controls */}
        <div className="col-span-12 flex flex-col gap-5 lg:col-span-5">
          {/* Presets */}
          <Section num="01" title="Cenário">
            <div className="grid grid-cols-2 gap-2">
              {STAGE_PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => update("preset", p)}
                  className={`border px-2 py-2 text-left transition-colors ${
                    config.preset === p
                      ? "border-butter bg-butter/10 text-paper"
                      : "border-border text-ink-muted hover:border-paper hover:text-paper"
                  }`}
                >
                  <span className="font-display text-sm leading-tight text-paper">
                    {PRESET_LABELS[p]}
                  </span>
                  <span className="block font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
                    {PRESET_BLURBS[p]}
                  </span>
                </button>
              ))}
            </div>
          </Section>

          {/* Lights */}
          <Section num="02" title="Spotlights">
            <div className="flex flex-col gap-3">
              <LightPicker
                label="Esquerda"
                value={config.lightLeft}
                onChange={(c) => update("lightLeft", c)}
              />
              <LightPicker
                label="Direita"
                value={config.lightRight}
                onChange={(c) => update("lightRight", c)}
              />
            </div>
          </Section>

          {/* Banner */}
          <Section num="03" title="Banner / Letreiro">
            <input
              type="text"
              maxLength={40}
              value={config.bannerText ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                update("bannerText", v || undefined);
              }}
              placeholder="ex: SÁBADO · 22h"
              className="border border-border bg-asphalt px-2 py-1.5 font-display text-base text-paper outline-none focus:border-butter"
              style={{ fontVariationSettings: '"opsz" 48, "wght" 500, "SOFT" 80' }}
            />
            <LightPicker
              label="Cor do letreiro"
              value={config.bannerColor}
              onChange={(c) => update("bannerColor", c)}
            />
          </Section>

          {/* Extras */}
          <Section num="04" title="Extras de palco">
            <Toggle label="Fumaça" value={config.smoke} onChange={(v) => update("smoke", v)} />
            <Toggle
              label="Riser da bateria"
              value={config.drumRiser}
              onChange={(v) => update("drumRiser", v)}
            />
            <Toggle
              label="Parede de amplificadores"
              value={config.ampStack}
              onChange={(v) => update("ampStack", v)}
            />
          </Section>

          {/* Slot weights — how much each slot pulls the band's sonic identity */}
          <Section num="05" title="Mixagem de som">
            <p className="font-mono text-[9px] leading-relaxed text-ink-dim">
              Peso de cada slot na identidade sonora da banda. 0 = não influencia.
            </p>
            {SLOT_ROLES.map((role) => (
              <WeightFader
                key={role}
                label={ROLE_LABELS[role]}
                value={config.slotWeights[role]}
                onChange={(v) => updateWeight(role, v)}
              />
            ))}
          </Section>
        </div>
      </div>

      <footer className="flex items-center justify-between border-t border-border pt-3">
        <Button variant="ghost" onClick={onReset}>
          resetar
        </Button>
        <div className="flex items-center gap-3">
          {dirty && (
            <span className="font-mono text-[10px] uppercase tracking-widest2 text-butter">
              · não salvo
            </span>
          )}
          <Button variant="primary" onClick={() => void onSave()} disabled={saving || !dirty}>
            {saving ? "salvando…" : "salvar palco"}
          </Button>
        </div>
      </footer>
    </div>
  );
}

function Section({
  num,
  title,
  children,
}: {
  num: string;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2 border-l-2 border-border pl-3">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[9px] tabular-nums text-vinyl">{num}</span>
        <span className="catalog-num text-paper">{title}</span>
      </div>
      {children}
    </div>
  );
}

function LightPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: LightColor;
  onChange: (c: LightColor) => void;
}): JSX.Element {
  return (
    <div>
      <p className="mb-1 font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {LIGHT_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            title={LIGHT_LABELS[c]}
            aria-label={LIGHT_LABELS[c]}
            className={`h-7 w-7 transition-all ${
              value === c
                ? "ring-2 ring-paper ring-offset-2 ring-offset-tape scale-110"
                : "hover:scale-105"
            }`}
            style={{
              background: LIGHT_HEX[c],
              border: "1px solid rgba(0,0,0,0.5)",
              boxShadow: `0 0 8px ${LIGHT_HEX[c]}66`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function WeightFader({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}): JSX.Element {
  const off = value === 0;
  return (
    <div className="flex items-center gap-3">
      <span className="w-20 font-mono text-[10px] uppercase tracking-widest2 text-paper">
        {label}
      </span>
      <input
        type="range"
        min={0}
        max={5}
        step={1}
        value={value}
        aria-label={`Peso de ${label}`}
        onChange={(e) => onChange(Number(e.target.value))}
        className="fader flex-1"
      />
      <span
        className={`w-6 text-right font-mono text-xs tabular-nums ${
          off ? "text-ink-dim" : "text-butter"
        }`}
      >
        {off ? "—" : String(value)}
      </span>
    </div>
  );
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`flex items-center justify-between border px-3 py-2 transition-colors ${
        value
          ? "border-butter bg-butter/10 text-paper"
          : "border-border text-ink-muted hover:border-paper hover:text-paper"
      }`}
    >
      <span className="font-mono text-[10px] uppercase tracking-widest2">{label}</span>
      <span
        className={`inline-block h-4 w-7 border ${
          value ? "border-butter bg-butter" : "border-border bg-tape"
        }`}
      >
        <span
          className="block h-3 w-3 transition-transform"
          style={{
            background: value ? "#08070a" : "#6a5640",
            transform: value ? "translateX(11px) translateY(0)" : "translateX(1px) translateY(0)",
            margin: "0.5px 0 0 0",
          }}
        />
      </span>
    </button>
  );
}
